/* eslint-disable react/jsx-no-bind */
import { useLocale, useResourceCreate, useResourceShow } from '@koopajs/react';
import { Box, Menu, MenuItem } from '@mui/material';
import { uniq } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { IMeeting } from 'types';
import { AllTopicType, smartTopicTypes } from 'utils/topicTypeArrays';
import { Lock, AddCircle, AddCircleOutline, Bolt, Title as TitleIcon, ExpandMore } from '@mui/icons-material';
import { theme } from 'components/Core/theme';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import cc from 'classnames';

interface IAddAdjacentTopicButtonProps {
  meeting: IMeeting;
  setTopicIdEditable: Dispatch<SetStateAction<string>> | ((arg0: string) => void);
  excludedTopicTypes?: AllTopicType[];
  adjacentTopicInformation: {
    id: string;
    position: 'previous' | 'next';
  };
  sx?: SxProps<Theme>;
}

export const AddAdjacentTopicButton: React.FC<IAddAdjacentTopicButtonProps> = (props) => {
  const { meeting, setTopicIdEditable, excludedTopicTypes, adjacentTopicInformation, sx } = props;

  const { t } = useLocale();
  const keyPrefix = 'Components.AddAdjacentTopicButton';

  const [isSmartTopicsMenuOpen, setIsSmartTopicsMenuOpen] = useState(false);
  const toggleSmartTopicsMenu = useCallback(() => {
    setIsSmartTopicsMenuOpen((prev) => !prev);
  }, []);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setMenuAnchorEl(event.currentTarget);
    },
    [adjacentTopicInformation.id, adjacentTopicInformation.position]
  );
  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setIsSmartTopicsMenuOpen(false);
  }, []);

  const checkIsIncludedTopicType = (topicType: AllTopicType): boolean => {
    return !excludedTopicTypes?.includes(topicType);
  };

  const uniqMeetingTopicTypes = uniq(meeting?.topics?.map((topic) => topic.type) || []);
  const missingSmartTopicTypes = smartTopicTypes
    .filter((item) => !uniqMeetingTopicTypes.includes(item))
    .filter(checkIsIncludedTopicType);
  const missingRegularNonGenericTopicTypes = (['miscellaneous'] as AllTopicType[])
    .filter((type) => !uniqMeetingTopicTypes.includes(type))
    .filter(checkIsIncludedTopicType);

  // workaround for loading state with custom reducer
  const { isProcessing } = useResourceShow({ path: '/meetings', id: meeting.id, useCache: true });

  const { createResource } = useResourceCreate({
    path: `/meetings/${meeting?.id}/topics?${adjacentTopicInformation.position}TopicId=${adjacentTopicInformation.id}`,
    customReducer: {
      path: { resourceType: '/meetings', resourceId: meeting.id },
      mapping: (oldMeeting: object, newMeeting: object) => {
        const newMeetingObject = newMeeting as IMeeting;
        return {
          ...oldMeeting,
          topics: newMeetingObject.topics
        };
      }
    }
  });

  const handleAddTopic = async (type: string): Promise<void> => {
    await createResource({ type, durationSeconds: 600 }, (id: string, resource: object | undefined) => {
      const newResource = resource as { newTopicId?: string };
      if (newResource.newTopicId) setTopicIdEditable(newResource.newTopicId);
    });
    handleCloseMenu();
  };

  // there's a TS error when passing this directly for some reason
  const label = t(keyPrefix + '.labelAddTopic');

  return (
    <Box sx={sx}>
      {/* 
        BUTTON
      */}
      <Box
        component="button"
        id="add-adjacent-topic-button"
        data-cy="add-adjacent-topic-button"
        aria-label={label}
        aria-controls={isMenuOpen ? 'add-adjacent-topic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        onClick={handleOpenMenu}
        disabled={isProcessing}
        className={cc({ 'menu-open': isMenuOpen })}
        sx={{
          position: 'relative',
          width: '100%',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          p: 0,
          // line (divider)
          '& .add-adjacent-topic-button_icon-parent-container::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: '1px',
            backgroundColor: theme.palette.divider,
            transform: 'translateY(-50%)'
          },
          '&.menu-open .add-adjacent-topic-button_icon-parent-container::before': {
            backgroundColor: theme.palette.primary.main
          },
          '& .add-adjacent-topic-button_icon-child-container': {
            visibility: 'hidden',
            zIndex: 1,
            backgroundColor: '#FFFFFF',
            px: 1
          },
          '&.menu-open .add-adjacent-topic-button_icon-child-container': {
            visibility: 'visible'
          },
          '& .add-adjacent-topic-button_icon': {
            color: theme.palette.primary.main
          },
          '&.menu-open .add-adjacent-topic-button_icon-child-container .add-adjacent-topic-button_icon.outlined':
            {
              display: 'none'
            },
          // HOVER
          '&:hover .add-adjacent-topic-button_icon-parent-container::before': {
            backgroundColor: theme.palette.primary.main
          },
          '&:hover .add-adjacent-topic-button_icon-child-container': {
            visibility: 'visible'
          },
          '&:hover .add-adjacent-topic-button_icon-child-container .add-adjacent-topic-button_icon.filled': {
            display: 'none'
          },
          // DISABLED
          '&:disabled': {
            cursor: 'unset'
          },
          '&.menu-open:disabled .add-adjacent-topic-button_icon-parent-container::before': {
            backgroundColor: theme.palette.customGrey.dark
          },
          '&:disabled .add-adjacent-topic-button_icon': {
            color: theme.palette.customGrey.dark
          },
          // MENU OPEN
          '&.menu-open .add-adjacent-topic-button_icon-child-container .add-adjacent-topic-button_icon.filled':
            {
              display: 'block'
            }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
          className="add-adjacent-topic-button_icon-parent-container"
        >
          <Box className="add-adjacent-topic-button_icon-child-container">
            <AddCircle
              className="add-adjacent-topic-button_icon filled"
              sx={{ color: theme.palette.primary.main }}
              aria-hidden
            />
            <AddCircleOutline
              className="add-adjacent-topic-button_icon outlined"
              sx={{ color: theme.palette.primary.main }}
              aria-hidden
            />
          </Box>
        </Box>
      </Box>

      {/* 
        MENU
      */}
      <Menu
        id="add-adjacent-topic-menu"
        data-cy="add-adjacent-topic-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'add-adjacent-topic-button'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {/* 
          REGULAR TOPICS
        */}
        {['information', ...missingRegularNonGenericTopicTypes].map((topicType) => {
          return (
            <MenuItem
              key={topicType}
              onClick={() => handleAddTopic(topicType)}
              disabled={isProcessing}
              data-cy={`add-adjacent-topic-menu-item-${topicType}`}
            >
              <TitleIcon sx={{ opacity: '0.54', mr: '6px' }} />
              {topicType === 'information'
                ? t(keyPrefix + '.newTopicMenuItem')
                : t('common:topicTypes.' + topicType)}
            </MenuItem>
          );
        })}

        {/* 
          SMART TOPIC SUB MENU HEADER
        */}
        {missingSmartTopicTypes.length > 0 && (
          <MenuItem
            onClick={toggleSmartTopicsMenu}
            id="toggle-smart-topic-menu"
            data-cy="toggle-smart-topic-menu"
            aria-expanded={isSmartTopicsMenuOpen}
            aria-controls={missingSmartTopicTypes.map((type) => `add_${type}`).join(' ')}
            disabled={isProcessing}
          >
            <>
              <svg width={0} height={0}>
                <defs>
                  <linearGradient
                    id={'boltIconGradient'}
                    x1="12.0038"
                    y1="3"
                    x2="12.0038"
                    y2="21"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor={theme.palette.automation.purple} />
                    <stop offset="100%" stopColor={theme.palette.automation.blue} />
                  </linearGradient>
                </defs>
              </svg>
              <Bolt sx={{ fill: `url(#boltIconGradient)`, mr: '6px' }} aria-hidden />
            </>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
            >
              {t(keyPrefix + '.smartTopicHeaderMenuItem')}
              <ExpandMore
                sx={{ transform: isSmartTopicsMenuOpen ? 'rotate(180deg)' : '', ml: 3 }}
                aria-hidden
              />
            </Box>
          </MenuItem>
        )}

        {/* 
          SMART TOPICS
        */}
        {missingSmartTopicTypes.map((topicType) => {
          return (
            <MenuItem
              key={topicType}
              id={`add_${topicType}`}
              data-cy={`add-adjacent-topic-menu-item-${topicType}`}
              onClick={() => handleAddTopic(topicType)}
              aria-labelledby="toggle-smart-topic-menu"
              sx={{
                // hides buttons but keeps their width so that the menu doesn't get wider when the submenu opens and the smart topic titles are wider
                visibility: isSmartTopicsMenuOpen ? 'visible' : 'hidden',
                height: isSmartTopicsMenuOpen ? 'auto' : 0,
                minHeight: isSmartTopicsMenuOpen ? 'auto' : 0, // removes space for mobile
                overflow: 'hidden', // fixes issue where scrollbar appears
                ...(isSmartTopicsMenuOpen ? {} : { py: 0 })
              }}
              disabled={isProcessing}
            >
              <Box sx={{ pl: `${24 + 6}px`, display: 'flex', alignItems: 'center' }}>
                {t('common:topicTypes.' + topicType)}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
